// AuthInitializer — silent session restore on app startup.
//
// Task 326.18. The refresh token is an httpOnly cookie now, so restoring a session is one call to
// the BFF: the browser attaches the cookie, the BFF answers with an access token, the user and the
// tenant list. Nothing is read out of storage — the previous version rebuilt the session from a
// refresh token, a user object and a tenant list kept in localStorage, all three of which could
// (and did) drift apart.
//
// isInitializing stays true until this completes, so ProtectedRoute holds routing instead of
// bouncing to /login on every browser refresh.
//
// Task 234: after a restore, silently switch to the last-used tenant if it is still available.

import React, { useEffect } from 'react';
import { refreshSession } from '@/api/session';
import { useAuthStore, LAST_TENANT_KEY } from '@/stores/authStore';

interface AuthInitializerProps {
  children: React.ReactNode;
}

function AuthInitializer({ children }: AuthInitializerProps): React.ReactElement {
  useEffect(() => {
    let cancelled = false;

    refreshSession()
      .then(async (session) => {
        if (cancelled) return;
        const { applySession, switchTenant } = useAuthStore.getState();
        applySession(session);

        const lastTenantId = localStorage.getItem(LAST_TENANT_KEY);
        if (!lastTenantId) return;

        if (!session.availableTenants.some((t) => t.tenantId === lastTenantId)) {
          // Stale membership — clean up the stored value.
          localStorage.removeItem(LAST_TENANT_KEY);
          return;
        }

        if (lastTenantId !== useAuthStore.getState().currentTenantId) {
          // Silently switch; on failure the operator simply stays on their default tenant.
          await switchTenant(lastTenantId).catch(() => undefined);
        }
      })
      .catch(() => {
        // No session (no cookie, expired, or revoked). Clearing here is what unblocks routing and
        // lets ProtectedRoute send the operator to /login — never a permanent spinner.
        if (!cancelled) useAuthStore.getState().logout();
      });

    return () => {
      cancelled = true;
    };
  }, []); // runs once on mount

  // Transparent wrapper — no visual output.
  return <>{children}</>;
}

export default AuthInitializer;
